/**
 * QuestionSetManagementPage
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8 - Question set management
 * Requirement 49.8: Multi-language CSV import support
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { QuestionSetList } from '../components/QuestionSetList';
import { QuestionSetForm } from '../components/QuestionSetForm';
import { QuestionSetCsvImport } from '../components/QuestionSetCsvImport';
import { questionSetApi } from '../api/questionSetApi';
import type { QuestionSet, QuestionSetListItem, CreateQuestionSetDTO } from '../types/questionSet.types';
import type { QuestionSetCsvParseResult } from '../utils/questionSetCsvParser';

type ViewMode = 'list' | 'create' | 'edit' | 'import';

export function QuestionSetManagementPage() {
  const [questionSets, setQuestionSets] = useState<QuestionSetListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingQuestionSet, setEditingQuestionSet] = useState<QuestionSet | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<QuestionSetListItem | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Fetch question sets
  const fetchQuestionSets = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await questionSetApi.getAll();
      setQuestionSets(data);
    } catch (error) {
      console.error('Failed to fetch question sets:', error);
      setSnackbar({
        open: true,
        message: '質問セットの取得に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestionSets();
  }, [fetchQuestionSets]);

  // Create handler
  const handleCreate = useCallback(() => {
    setEditingQuestionSet(null);
    setViewMode('create');
  }, []);

  // Import handler (Requirement 49.8)
  const handleImportClick = useCallback(() => {
    setViewMode('import');
  }, []);

  // CSV Import handler (Requirement 49.8)
  const handleCsvImport = useCallback(
    async (parseResult: QuestionSetCsvParseResult) => {
      try {
        setIsSubmitting(true);
        // Create question set from CSV data
        const createData: CreateQuestionSetDTO = {
          title: parseResult.questionSetTitle || '新規質問セット',
          description: `CSVからインポートされた質問セット${parseResult.isMultiLanguage ? '（多言語対応）' : ''}`,
          questions: parseResult.questions.map((q, index) => ({
            text: q.text,
            order: index,
            textEn: q.textEn,
            textId: q.textId,
            textMn: q.textMn,
            textNe: q.textNe,
            textVi: q.textVi,
            answerChoices: [
              {
                text: q.choiceA.text,
                thinkingTypeCode: q.choiceA.thinkingTypeCode,
                order: 0,
                textEn: q.choiceA.textEn,
                textId: q.choiceA.textId,
                textMn: q.choiceA.textMn,
                textNe: q.choiceA.textNe,
                textVi: q.choiceA.textVi,
              },
              {
                text: q.choiceB.text,
                thinkingTypeCode: q.choiceB.thinkingTypeCode,
                order: 1,
                textEn: q.choiceB.textEn,
                textId: q.choiceB.textId,
                textMn: q.choiceB.textMn,
                textNe: q.choiceB.textNe,
                textVi: q.choiceB.textVi,
              },
            ],
          })),
        };

        await questionSetApi.create(createData);
        setSnackbar({
          open: true,
          message: `${parseResult.questions.length} 件の質問をインポートしました${parseResult.isMultiLanguage ? '（多言語対応）' : ''}`,
          severity: 'success',
        });
        setViewMode('list');
        await fetchQuestionSets();
      } catch (error) {
        console.error('Failed to import question set:', error);
        setSnackbar({
          open: true,
          message: 'インポートに失敗しました',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchQuestionSets]
  );

  // Edit handler
  const handleEdit = useCallback(async (questionSetItem: QuestionSetListItem) => {
    try {
      // Fetch full question set details for editing
      const fullQuestionSet = await questionSetApi.getById(questionSetItem.id);
      setEditingQuestionSet(fullQuestionSet);
      setViewMode('edit');
    } catch (error) {
      setSnackbar({
        open: true,
        message: '質問セットの取得に失敗しました',
        severity: 'error',
      });
    }
  }, []);

  // Cancel handler
  const handleCancel = useCallback(() => {
    setEditingQuestionSet(null);
    setViewMode('list');
  }, []);

  // Submit handler (create/update)
  const handleSubmit = useCallback(
    async (data: CreateQuestionSetDTO) => {
      try {
        setIsSubmitting(true);
        if (viewMode === 'edit' && editingQuestionSet) {
          await questionSetApi.update(editingQuestionSet.id, data);
          setSnackbar({
            open: true,
            message: '質問セットを更新しました',
            severity: 'success',
          });
        } else {
          await questionSetApi.create(data);
          setSnackbar({
            open: true,
            message: '質問セットを作成しました',
            severity: 'success',
          });
        }
        setViewMode('list');
        setEditingQuestionSet(null);
        await fetchQuestionSets();
      } catch (error) {
        console.error('Failed to save question set:', error);
        setSnackbar({
          open: true,
          message: '保存に失敗しました',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [viewMode, editingQuestionSet, fetchQuestionSets]
  );

  // Delete handlers
  const handleDeleteClick = useCallback((questionSetItem: QuestionSetListItem) => {
    setDeleteTarget(questionSetItem);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      await questionSetApi.delete(deleteTarget.id);
      setSnackbar({
        open: true,
        message: '質問セットを削除しました',
        severity: 'success',
      });
      setDeleteTarget(null);
      await fetchQuestionSets();
    } catch (error) {
      console.error('Failed to delete question set:', error);
      setSnackbar({
        open: true,
        message: '削除に失敗しました',
        severity: 'error',
      });
    }
  }, [deleteTarget, fetchQuestionSets]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  // Toggle publish handler
  const handleTogglePublish = useCallback(
    async (id: string, publish: boolean) => {
      try {
        if (publish) {
          await questionSetApi.publish(id);
          setSnackbar({
            open: true,
            message: '質問セットを公開しました',
            severity: 'success',
          });
        } else {
          await questionSetApi.unpublish(id);
          setSnackbar({
            open: true,
            message: '質問セットを非公開にしました',
            severity: 'success',
          });
        }
        await fetchQuestionSets();
      } catch (error) {
        console.error('Failed to toggle publish status:', error);
        setSnackbar({
          open: true,
          message: '公開状態の変更に失敗しました',
          severity: 'error',
        });
      }
    },
    [fetchQuestionSets]
  );

  // Close snackbar
  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {viewMode === 'list' && (
        <QuestionSetList
          questionSets={questionSets}
          isLoading={isLoading}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onTogglePublish={handleTogglePublish}
          onImport={handleImportClick}
        />
      )}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <QuestionSetForm
          questionSet={editingQuestionSet || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      )}
      {viewMode === 'import' && (
        <QuestionSetCsvImport
          onImport={handleCsvImport}
          onCancel={handleCancel}
          isImporting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
        <DialogTitle>質問セットの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{deleteTarget?.title}」を削除しますか？この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
